import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { WizardStatus } from '@risksmart-app/domain/src/types/consts';
import { AssessmentActivityStatus as Assessment_Activity_Status_Enum } from '@risksmart-app/domain/src/types/consts/assessment-activity-status';
import { AssessmentActivityType as Assessment_Activity_Type_Enum } from '@risksmart-app/domain/src/types/consts/assessment-activity-type';
import { useTranslation } from 'react-i18next';

import { useUpdateAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useUpdateAssessmentActivityWithLinkedItems';
import { handleError } from '@/utils/errorUtils';

import { useWizardStore } from '../store/useWizardStore';
import { useNavigateToStep } from './useNavigateToStep';

export interface UseStartWizardArgs {
  basePath: string;
  riskId: string;
  onStartNew: () => void;
}

export interface UseStartWizard {
  isWizardInProgress: boolean;
  // True during the resume mutation OR while the store's hydrated risk still
  // describes a different risk (a stale read after navigating — the store is
  // a module singleton nothing resets on route change).
  loading: boolean;
  startWizard: () => Promise<void>;
}

// Shared by the legacy WizardButton and the object toolbar's `wizard` action.
// Must branch on isWizardInProgress rather than always calling onStartNew —
// starting a second RCSA against a risk that already has one is rejected by
// the insert (CUS-363).
export const useStartWizard = ({
  basePath,
  riskId,
  onStartNew,
}: UseStartWizardArgs): UseStartWizard => {
  const wizardStatus = useWizardStore((s) => s.wizardStatus);
  const assessmentId = useWizardStore((s) => s.assessmentId);
  const activityId = useWizardStore((s) => s.activityId);
  const currentStep = useWizardStore((s) => s.currentStep);
  const storeRiskId = useWizardStore((s) => s.risk.riskId);
  const { navigateToStep } = useNavigateToStep();
  const { addNotification } = useNotifications();
  const { user } = useRisksmartUser();
  const [toolsContent] = useTools();
  const { t } = useTranslation('common', { keyPrefix: 'wizard' });

  const { updateAssessmentActivity, loading: mutationLoading } =
    useUpdateAssessmentActivityWithLinkedItems();

  // The store is a module singleton — still describes the previous risk until
  // useInitiateWizard rehydrates it for this one.
  const isRiskDataStale = storeRiskId !== riskId;
  const isWizardInProgress =
    !isRiskDataStale && wizardStatus === WizardStatus.InProgress;

  const startWizard = async () => {
    if (isRiskDataStale) {
      return;
    }

    // The wizard sidebar is already open on this risk — nothing to start.
    if (toolsContent === 'wizard') {
      return;
    }

    if (!isWizardInProgress) {
      onStartNew();

      return;
    }

    if (!user) {
      return;
    }

    try {
      await updateAssessmentActivity({
        Id: activityId,
        Status: Assessment_Activity_Status_Enum.InProgress,
        ActivityType: Assessment_Activity_Type_Enum.Task,
        // COI-683: the risk link is the `RiskId` column, not a generic linked
        // item — sending it here too double-writes the (directional) PK.
        LinkedItemIds: [],
        ParentId: assessmentId,
        IsWizardAction: true,
        OwnerUserIds: [user.userId],
        OwnerGroupIds: [],
      });
      navigateToStep(basePath, currentStep);
    } catch (error) {
      handleError(error);
      addNotification({
        type: 'error',
        content: t('resumeError'),
      });
    }
  };

  return {
    isWizardInProgress,
    loading: mutationLoading || isRiskDataStale,
    startWizard,
  };
};
